/**
 * Workflow Step Configuration
 *
 * Centralized metadata for all workflow steps providing user guidance,
 * focus points, and contextual help for novice/intermediate users.
 */

import { WorkflowStep } from '@/contexts/WorkflowContext';

export interface StepConfig {
  number: number;
  name: string;
  shortDescription: string;
  description: string;
  focusPoints: string[];
  dontWorryAbout: string[];
  outputDescription: string;
  nextStepPreview: string;
  audience: string;
}

export const WORKFLOW_STEPS: Record<WorkflowStep, StepConfig> = {
  discovery: {
    number: 1,
    name: 'Discovery',
    shortDescription: 'Define the problem',
    description:
      'Define the problem your agent will solve. Focus on WHAT you need, not HOW to build it.',
    focusPoints: [
      'What problem are you solving?',
      'Who are the users of this agent?',
      'What does success look like?',
      'What constraints or limitations exist?',
    ],
    dontWorryAbout: [
      'Technical implementation details',
      'Specific tools or APIs',
      'Which AI model to use',
      'How the code will be structured',
    ],
    outputDescription:
      'A clear problem statement with goals, users, and success criteria',
    nextStepPreview:
      "Next, we'll design your agent's behavior and personality.",
    audience:
      'Business stakeholders, product owners, anyone who understands the problem',
  },

  design: {
    number: 2,
    name: 'Design',
    shortDescription: 'Define agent behavior',
    description:
      'Design how your agent behaves, communicates, and operates. Think of this as creating your agent\'s "character sheet."',
    focusPoints: [
      'What personality should the agent have?',
      'What can the agent do? What are its capabilities?',
      'What should the agent avoid or refuse to do?',
      'How autonomous should it be?',
    ],
    dontWorryAbout: [
      'Specific APIs or integrations',
      'Database schemas',
      'Which AI model to use',
      'Deployment infrastructure',
    ],
    outputDescription:
      'An agent design specification with name, persona, goals, capabilities, and boundaries',
    nextStepPreview:
      "Next, we'll figure out if you need one agent or multiple agents working together.",
    audience: 'Product owners, UX designers, business analysts',
  },

  multiagent: {
    number: 3,
    name: 'Multi-Agent Planning',
    shortDescription: 'Plan agent architecture',
    description:
      'Determine if you need multiple agents and how they work together. Sometimes one agent can do it all; other times you need a team of specialists.',
    focusPoints: [
      'Is one agent enough, or do you need specialists?',
      'If multiple agents, how do they divide the work?',
      'How do agents hand off tasks to each other?',
      'Who is the "manager" that coordinates?',
    ],
    dontWorryAbout: [
      'Writing any code',
      'Specific frameworks or libraries',
      'Deployment details',
      'Cost optimization',
    ],
    outputDescription:
      'An architecture showing how many agents you need and how they coordinate',
    nextStepPreview:
      "Next, we'll specify the technical details like tools, memory, and AI model selection.",
    audience: 'System architects, technical leads',
  },

  technical: {
    number: 4,
    name: 'Technical Architecture',
    shortDescription: 'Specify technical details',
    description:
      'Define the technical components needed to build your agent(s). Now we get into the specifics of tools, memory, and integrations.',
    focusPoints: [
      'What tools and APIs does the agent need?',
      'What information should the agent remember?',
      'Which AI model is best for this use case?',
      'What systems does the agent integrate with?',
    ],
    dontWorryAbout: [
      'Writing actual code',
      'Deployment pipelines',
      'Monitoring setup',
      'Production scaling',
    ],
    outputDescription:
      'A technical specification with tools, memory architecture, model selection, and integrations',
    nextStepPreview: "Next, we'll generate the actual code for your agent.",
    audience: 'Developers, engineers, technical architects',
  },

  codegen: {
    number: 5,
    name: 'Code Generation',
    shortDescription: 'Generate agent code',
    description:
      "Generate the actual agent code based on all previous specifications. You'll get working code you can customize and extend.",
    focusPoints: [
      'Review the generated code structure',
      'Understand the key components',
      'Identify areas you may want to customize',
      'Check that it matches your requirements',
    ],
    dontWorryAbout: [
      "Production deployment (that's next)",
      'Scaling configuration',
      'Monitoring and alerting',
      'Cost optimization',
    ],
    outputDescription:
      'Working agent code with configuration files and test scaffolding',
    nextStepPreview:
      "Finally, we'll plan how to deploy and operate your agent in production.",
    audience: 'Developers',
  },

  operations: {
    number: 6,
    name: 'Operations Planning',
    shortDescription: 'Plan deployment',
    description:
      'Plan how to deploy, monitor, and maintain your agent in production. This is the final step to get your agent running reliably.',
    focusPoints: [
      'Where will the agent run?',
      'How will you monitor its performance?',
      'How will you handle failures?',
      'How will you update and improve it?',
    ],
    dontWorryAbout: ["This is the final step - you've covered everything!"],
    outputDescription:
      'A deployment plan with monitoring setup, runbooks, and maintenance procedures',
    nextStepPreview: "You're done! Your agent is ready for deployment.",
    audience: 'DevOps, SRE, operations teams',
  },
};

/**
 * Get step configuration by step key
 */
export function getStepConfig(step: WorkflowStep): StepConfig {
  return WORKFLOW_STEPS[step];
}

/**
 * Get all steps in order
 */
export function getOrderedSteps(): StepConfig[] {
  return Object.values(WORKFLOW_STEPS).sort((a, b) => a.number - b.number);
}

/**
 * Get step by number (1-indexed)
 */
export function getStepByNumber(num: number): StepConfig | undefined {
  return Object.values(WORKFLOW_STEPS).find((step) => step.number === num);
}
